<template>
  <div class="dsd-root sc-page-root">
    <FiPageRail title="Driver Manager Dashboard">
      <template #actions>
        <b-button v-if="canEditSettings" size="sm" variant="outline-secondary" @click="openSettings" title="Manage Driver Manager settings" aria-label="Settings">
          <i class="fa fa-cog"></i>&nbsp;Settings
        </b-button>
      </template>
    </FiPageRail>

    <!-- settings modal (weights + conditional coloring), ?settings=1 deep link;
         the body component re-hydrates on every open via the v-if -->
    <b-modal id="dsd-settings-modal" size="xl" scrollable hide-footer
             modal-class="dsd-settings-modal"
             @show="settingsModalShown = true" @hidden="onSettingsHidden">
      <template slot="modal-title">Driver Manager settings</template>
      <driver-manager-settings v-if="settingsModalShown" :initial-tab="settingsInitialTab" @saved="settingsSavedAny = true" />
    </b-modal>

    <!-- date preset chips + expanding custom range; every change reloads the
         data itself (app-wide auto-refresh doctrine), so there is no manual
         refresh button -->
    <div class="sc-toolbar" :class="{ 'sc-toolbar--custom': showCustomRange }">
      <div class="sc-toolbar-group dsd-date-presets" role="group" aria-label="Date range">
        <button v-for="m in quickRangeOptions" :key="'qr-' + m" type="button"
          :class="['sc-chip', { 'sc-chip--active': !customRangeOpen && activeQuickRangeMonths === m }]"
          :aria-pressed="!customRangeOpen && activeQuickRangeMonths === m ? 'true' : 'false'"
          @click="setDatePreset(m)">{{ m }}m</button>
        <button type="button" :class="['sc-chip', { 'sc-chip--active': showCustomRange }]"
          :aria-pressed="showCustomRange ? 'true' : 'false'"
          @click="setDatePreset('custom')">Custom</button>
      </div>
      <div class="dsd-date-fields">
        <date-picker class="sc-control dsd-date-field" v-model="details_fromDate" :config="optionsFrom" :placeholder="details_fromDate"/>
        <span class="dsd-date-sep">to</span>
        <date-picker class="sc-control dsd-date-field" v-model="details_toDate" :config="optionsTo" :placeholder="details_toDate"/>
      </div>
      <template v-if="!isCmTenant">
        <div class="sc-toolbar-group">
          <span class="dsd-toolbar-label">Status</span>
          <b-form-select v-model="selected_status_filter" :options="status_filter_options" class="sc-control sc-control-select"/>
        </div>
      </template>
    </div>

    <!-- the full-page spinner only shows before the first payload; reloads keep
         the previous render visible at reduced opacity (stale-hold) -->
    <div v-if="loading && !drivers.length && !error" style="text-align: center; margin: 50px;">
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>
    <div v-else-if="error" class="text-center text-danger" style="margin: 50px;">
      <p>{{ error }}</p>
      <b-button size="sm" variant="outline-secondary" @click="load_data()">Retry</b-button>
    </div>
    <div v-else-if="!drivers.length" class="text-center text-muted" style="margin: 50px;">
      <h4>No pre-calculated data for this period.</h4>
      <p>Try a different date range.</p>
    </div>

    <div v-else :class="{ 'dsd-stale': loading }">
      <fi-tab-bar v-model="activeTab" :tabs="tabDefs" id-prefix="dsd-tab" aria-label="Driver Manager Dashboard sections" />

      <div class="mt-3">
        <!-- FLEET OVERVIEW -->
        <div v-show="activeTab === 'fleet'" role="tabpanel" aria-labelledby="dsd-tab-fleet">
          <div class="ad-chart-card ad-chart-card--wide mb-3">
            <div class="ad-chart-title">Score Distribution</div>
            <div class="ad-chart-canvas-wrap" style="height: 220px;"><canvas ref="chScores"></canvas></div>
          </div>

          <div class="scorecard-metrics-row dsd-metrics-row justify-content-center">
            <b-card bg-variant="light" class="metric-card metric-card-inline">
              <h5>Total Drivers</h5>
              <h2>{{ totalDrivers }}</h2>
              <span class="text-muted" style="font-size: 0.78rem;">in selected period</span>
            </b-card>
            <b-card bg-variant="light" class="metric-card metric-card-inline">
              <h5>
                Fleet Avg Combined Score
                <i class="fa fa-question-circle text-muted" style="font-size: 0.75rem; cursor: help;"
                   v-b-tooltip.hover="'A single 0-100 score blending each driver\'s weighted KPIs. In Driver Manager Settings, the Combined Score Weights tab adjusts how much each KPI counts and the Conditional Coloring tab sets what counts as a good/bad value.'"
                   tabindex="0"></i>
              </h5>
              <h2 :class="getCompiledScoreTextClass(fleetAvgScore, fleet_average && fleet_average.compiled_score_tier)">{{ formatNumber(fleetAvgScore) }}</h2>
              <span class="text-muted" style="font-size: 0.78rem;">combined KPI / 100</span>
            </b-card>
            <b-card bg-variant="light" class="metric-card metric-card-inline">
              <h5>Elite</h5>
              <h2 class="sc-tier-text--elite">{{ tierCounts.Elite }}</h2>
              <span class="text-muted" style="font-size: 0.78rem;">{{ tierPct('Elite') }}% of fleet</span>
            </b-card>
            <b-card bg-variant="light" class="metric-card metric-card-inline">
              <h5>Solid</h5>
              <h2 class="sc-tier-text--solid">{{ tierCounts.Solid }}</h2>
              <span class="text-muted" style="font-size: 0.78rem;">{{ tierPct('Solid') }}% of fleet</span>
            </b-card>
            <b-card bg-variant="light" class="metric-card metric-card-inline">
              <h5>Fair</h5>
              <h2 class="sc-tier-text--coaching">{{ tierCounts['Fair'] }}</h2>
              <span class="text-muted" style="font-size: 0.78rem;">{{ tierPct('Fair') }}% of fleet</span>
            </b-card>
            <b-card bg-variant="light" class="metric-card metric-card-inline">
              <h5>High Risk</h5>
              <h2 class="sc-tier-text--highrisk">{{ tierCounts['High Risk'] }}</h2>
              <span class="text-muted" style="font-size: 0.78rem;">{{ tierPct('High Risk') }}% of fleet</span>
            </b-card>
            <b-card bg-variant="light" class="metric-card metric-card-inline">
              <h5>Insufficient Data</h5>
              <h2 class="text-insufficient">{{ tierCounts['Insufficient Data'] }}</h2>
              <span class="text-muted" style="font-size: 0.78rem;">{{ tierPct('Insufficient Data') }}% of fleet</span>
            </b-card>
          </div>

          <div class="dsd-chart-grid">
            <div class="ad-chart-card">
              <div class="ad-chart-title">Tier Breakdown <span class="text-muted" style="font-size: 0.7rem; font-weight: normal;">(click a slice to open those drivers in the Roster List)</span></div>
              <div class="ad-chart-canvas-wrap" style="height: 260px;"><canvas ref="chTiers"></canvas></div>
            </div>
            <div class="ad-chart-card">
              <div class="ad-chart-title">% of Miles Driven by Tier</div>
              <div class="ad-chart-canvas-wrap" style="height: 260px;"><canvas ref="chKpis"></canvas></div>
            </div>
            <div class="ad-chart-card">
              <div class="ad-chart-title">Fleet Avg vs Elite Avg
                <i class="fa fa-question-circle text-muted" style="font-size: 0.75rem; cursor: help;"
                   v-b-tooltip.hover="'How the whole active fleet compares to just this period\'s Elite-tier drivers on the same KPIs that feed the individual driver radar chart.'"
                   tabindex="0"></i>
              </div>
              <div v-if="eliteAverageMetrics" class="ad-chart-canvas-wrap" style="height: 260px;"><canvas ref="chFleetRadar"></canvas></div>
              <div v-else class="text-center text-muted" style="padding: 40px 0; font-size: 0.85rem;">No Elite-tier drivers this period.</div>
            </div>
          </div>

          <!-- the per-driver leaderboard table that used to live here moved to
               its own Roster List page (driver_manager_roster_list.vue); the
               Tier Breakdown chart's slice click deep-links there pre-filtered -->
        </div>

        <!-- FLAGGED DRIVERS -->
        <div v-show="activeTab === 'flagged'" role="tabpanel" aria-labelledby="dsd-tab-flagged">
          <div class="dsd-chip-row">
            <button class="dsd-chip dsd-chip-crit" :class="{ active: coachFilters.includes('critical') }" @click="toggleCoachFilter('critical')">
              <span class="dsd-chip-count">{{ flaggedCounts.critical }}</span> Critical Flags
            </button>
            <button class="dsd-chip dsd-chip-warn" :class="{ active: coachFilters.includes('warning') }" @click="toggleCoachFilter('warning')">
              <span class="dsd-chip-count">{{ flaggedCounts.warning }}</span> Warnings Only
            </button>
            <button class="dsd-chip dsd-chip-clean" :class="{ active: coachFilters.includes('clean') }" @click="toggleCoachFilter('clean')">
              <span class="dsd-chip-count">{{ flaggedCounts.clean }}</span> Clean
            </button>
            <button class="dsd-chip dsd-chip-low" :class="{ active: coachFilters.includes('lowActivity') }" @click="toggleCoachFilter('lowActivity')">
              <span class="dsd-chip-count">{{ flaggedCounts.lowActivity }}</span> Insufficient Data
            </button>
          </div>
          <p class="text-center text-muted" style="font-size: 0.78rem; margin: -6px 0 14px;">Click a chip to filter — click multiple to combine (e.g. Critical + Warnings)</p>

          <div class="d-flex align-items-center justify-content-center flex-wrap mb-3" style="gap: 8px;">
            <b-form-input v-model="coachSearch" placeholder="Search driver..." class="dsd-filter-control" style="max-width: 220px;" size="sm"/>
            <b-button v-if="coachFilters.length || coachSearch" size="sm" variant="outline-secondary" @click="clearCoachFilters()">Clear Filters</b-button>
            <span class="text-muted ml-2" style="font-size: 0.85rem;">{{ filteredFlaggedList.length }} drivers</span>
          </div>

          <div class="dsd-coach-grid">
            <b-card v-for="d in filteredFlaggedList" :key="d.driver_id || d.driver_name" class="dsd-coach-card" :class="coachCardClass(d)">
              <div class="d-flex align-items-center mb-2">
                <div>
                  <div style="font-weight: bold; font-size: 1.05rem;">{{ d.driver_name }}</div>
                  <div class="text-muted" style="font-size: 0.78rem;">
                    <span :class="getCompiledTierBadgeClass(d.compiled_score_tier)">{{ d.compiled_score_tier }}</span>
                    <template v-if="rankOf(d)">&nbsp;Rank #{{ rankOf(d) }}</template>
                    <template v-else>&nbsp;Not ranked</template>
                    &nbsp;&middot;&nbsp; {{ d.bus_days || 0 }} days
                  </div>
                </div>
                <div class="ml-auto" style="font-weight: 800; font-size: 1.4rem;" :class="getCompiledScoreTextClass(d.compiled_score, d.compiled_score_tier)">{{ formatNumber(d.compiled_score, 0) }}</div>
              </div>
              <div v-if="!isActive(d)" class="dsd-lowactivity-msg">— Not enough activity this period to evaluate KPIs.</div>
              <template v-else-if="getFlags(d).length">
                <div v-for="f in getFlags(d)" :key="f.key" class="dsd-flag-item" :class="'dsd-flag-' + f.severity">
                  <strong>{{ f.severity === 'critical' ? '⚠' : '⚡' }} {{ f.label }}</strong>
                  <div class="text-muted" style="font-size: 0.8rem;">{{ f.note }}</div>
                </div>
              </template>
              <div v-else class="dsd-clean-msg">✓ All KPIs within thresholds.</div>
              <b-button block size="sm" variant="outline-primary" class="mt-2" @click="openDriver(d.driver_id || d.driver_name)">📊 View Profile</b-button>
            </b-card>
            <div v-if="!filteredFlaggedList.length" class="text-center text-muted" style="grid-column: 1 / -1; padding: 40px 0;">No drivers match the current filters.</div>
          </div>
        </div>

        <!-- TRENDS -->
        <div v-show="activeTab === 'trends'" role="tabpanel" aria-labelledby="dsd-tab-trends">
          <div class="d-flex align-items-center justify-content-center flex-wrap mb-3">
            <span style="font-weight: bold; font-size: 1.1rem;">Driver:&nbsp;</span>
            <b-form-select v-model="trendsDriverId" :options="trendsDriverOptions" class="dsd-driver-select" style="font-size: 16px; font-weight: bold; width: 280px;"/>
          </div>

          <div v-if="loadingTrends" style="text-align: center; margin: 50px;">
            <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
          </div>
          <div v-else-if="trendsError" class="text-center text-danger" style="margin: 50px;">{{ trendsError }}</div>
          <div v-else-if="!trendsMonths.length" class="text-center text-muted" style="margin: 50px;">
            <h4>No month-by-month trend data for this period.</h4>
            <p>Trends need at least one fully-completed past month in the selected range.</p>
          </div>
          <template v-else>
            <p v-if="trendsDriverId && !trendsHasDriverData" class="text-center text-muted" style="font-size: 0.85rem;">
              No activity found for this driver in the selected months — showing fleet-wide trends only.
            </p>
            <div class="dsd-chart-grid">
              <div class="ad-chart-card ad-chart-card--wide">
                <div class="ad-chart-title">{{ trendsDriverId ? `${trendsDriverName} vs Fleet — Combined Score & Safety by Month` : 'Fleet Avg Combined Score & Safety Score by Month' }}</div>
                <div class="ad-chart-canvas-wrap" style="height: 260px;"><canvas ref="chTrendScore"></canvas></div>
              </div>
              <div class="ad-chart-card ad-chart-card--wide">
                <div class="ad-chart-title">{{ trendsDriverId ? `Tier Distribution by Month (Fleet-wide) with ${trendsDriverName}'s Tier` : 'Tier Distribution by Month (Fleet-wide)' }}</div>
                <div class="ad-chart-canvas-wrap" style="height: 260px;"><canvas ref="chTrendTiers"></canvas></div>
              </div>
              <div class="ad-chart-card ad-chart-card--wide">
                <div class="ad-chart-title">{{ trendsOpsChartTitle }}</div>
                <div class="dsd-ops-field-row">
                  <span v-for="m in activeTrendsOpsMetrics" :key="m.key" class="dsd-ops-field-chip">
                    {{ m.label }}
                    <button type="button" class="dsd-ops-field-remove" @click="removeOpsField(m.key)" title="Remove from chart">&times;</button>
                  </span>
                  <select v-if="trendsOpsAvailableMetrics.length" v-model="opsFieldToAdd" class="dsd-ops-field-add custom-select custom-select-sm" @change="addOpsField(opsFieldToAdd)">
                    <option value="" disabled>+ Add metric&hellip;</option>
                    <option v-for="m in trendsOpsAvailableMetrics" :key="m.key" :value="m.key">{{ m.label }}</option>
                  </select>
                </div>
                <div v-if="!activeTrendsOpsMetrics.length" class="text-center text-muted" style="padding: 40px 0;">Pick at least one metric above to chart it.</div>
                <div v-else class="ad-chart-canvas-wrap" style="height: 260px;"><canvas ref="chTrendOps"></canvas></div>
              </div>
            </div>
          </template>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment'
import { Chart, registerables } from 'chart.js'
import { justDate } from '../helpers/dates'
import { get_all_driver_scorecards, get_driver_scorecard_trends } from '../networking/tbn_apis'
import { cm_get_all_driver_scorecards, cm_get_driver_scorecard_trends } from '../networking/apis'
import { mergeThresholds, getCompiledScoreClass, tierClass, getTierColor } from '../utils/driver_manager_thresholds'
import { buildFirstOfMonthDates, buildLastOfMonthOrTodayDates, defaultScorecardRange } from '../utils/driver_manager_dates'
import { buildBadnessPools, getDriverFlags, isActiveDriver, metricBadnessPercentile } from '../utils/scorecard_flags'
import FiTabBar from './fi_tab_bar.vue'
import DriverManagerSettings from './driver_manager_settings.vue'

Chart.register(...registerables)

// keep chart tooltips consistent with the rest of the dashboard: exactly 2
// decimal places, never the raw floating-point precision from the API.
Chart.defaults.plugins.tooltip.callbacks.label = function(context) {
  const parsed = context.parsed
  // Bar/line charts parse to {x, y}, radar to {r}, but pie/doughnut (Tier Breakdown)
  // parse to a plain number — falling through to .y/.r on a number is undefined,
  // which is why that chart's tooltip used to just say "undefined".
  const raw = parsed && typeof parsed === 'object'
    ? (parsed.r !== undefined ? parsed.r : parsed.y)
    : parsed
  const label = context.dataset.label ? `${context.dataset.label}: ` : ''
  if (typeof raw !== 'number') return `${label}${raw}`
  // Whole numbers (e.g. a doughnut slice's driver count) read oddly as "5.00"
  return `${label}${Number.isInteger(raw) ? raw : raw.toFixed(2)}`
}

const TIER_ORDER = ['Elite', 'Solid', 'Fair', 'High Risk']
const INSUFFICIENT_DATA_TIER = 'Insufficient Data'
// Ranking-only views (peer comparisons, percentile rank) intentionally exclude
// Insufficient Data drivers via TIER_ORDER above. Charts that are meant to represent
// the whole fleet (Tier Breakdown doughnut, miles-by-tier bar) need the tier included so
// low-activity drivers don't silently vanish from the dataset and undercount the total.
const CHART_TIER_ORDER = [...TIER_ORDER, INSUFFICIENT_DATA_TIER]
// Selectable metrics for the Trends tab's "Ops" line chart (FI-2018 follow-up: the
// original hardcoded Idling/DVIR/Weekend trio felt arbitrary, so the whole catalog of
// scorecard fields is offered and the user picks what they want to track month over
// month). `percent` fields share the fixed 0-100 axis with each other; as soon as a
// non-percent field (a rate like harsh braking/10k mi) is added, the axis switches to
// auto-scaled min:0 so it isn't flattened against a 100 ceiling it'll never reach.
// each metric carries a light hue (the long-standing set) plus a colorDark
// stepped for the midnight card, resolved at build time via opsColor() --
// dark mode is selected, never flipped (utils/alerts_analytics.js practice).
const TREND_OPS_METRICS = [
  { key: 'risk_score', label: 'Safety Score', color: '#007bff', colorDark: '#3987e5', percent: true },
  { key: 'idling_percentage', label: 'Idling %', color: '#ffc107', colorDark: '#bd8524', percent: true },
  { key: 'dvir_completion_rate', label: 'DVIR %', color: '#17a2b8', colorDark: '#4db6c7', percent: true },
  { key: 'weekend_availability_percentage', label: 'Weekend Availability %', color: '#6f42c1', colorDark: '#9085e9', percent: true, cmHidden: true },
  { key: 'speeding_percentage', label: 'Speeding %', color: '#dc3545', colorDark: '#e46666', percent: true },
  // MPG typically runs 0-10, which plots as a near-flat line against the 0-100 axis
  // shared with the percent fields above — scale it up for the chart so its shape is
  // actually visible next to them. The tooltip callback in buildTrendsChartsInner()
  // divides back out so the number shown on hover is still the real MPG.
  { key: 'miles_per_gallon', label: 'MPG', color: '#20c997', colorDark: '#35c49d', percent: false, scale: 10 },
  { key: 'distracted_driving_per_10k_miles', label: 'Distracted Driving /10k mi', color: '#fd7e14', colorDark: '#d95926', percent: false },
  { key: 'following_distance_per_10k_miles', label: 'Following Distance /10k mi', color: '#e83e8c', colorDark: '#d55181', percent: false },
  { key: 'harsh_braking_per_10k_miles', label: 'Harsh Braking /10k mi', color: '#6610f2', colorDark: '#a678de', percent: false },
  { key: 'customer_requests_per_10k_miles', label: 'Cust. Requests /10k mi', color: '#0d6efd', colorDark: '#6ba3ee', percent: false, cmHidden: true },
]
// Shared axis labels for both radar charts (individual-vs-fleet-avg and
// fleet-avg-vs-elite-avg) — see radarMetricValues().
const RADAR_LABELS = ['Safety Score', 'No Speeding', 'No Distraction', 'Following Dist', 'Low Idling', 'MPG']
// Raw KPI keys averaged across Elite-tier drivers for the fleet-overview radar chart —
// same six inputs radarMetricValues() normalizes, in the same order as RADAR_LABELS.
const RADAR_METRIC_KEYS = ['risk_score', 'speeding_percentage', 'distracted_driving_per_10k_miles', 'following_distance_per_10k_miles', 'idling_percentage', 'miles_per_gallon']
// Flagged-driver rules (FLAG_METRICS, the absolute-gate + relative-severity
// logic, and the badness percentile) live in utils/scorecard_flags.js since
// the Coaching Portal's Flagged Drivers panel shares them — this tab and that
// panel must always agree on who is flagged and why. The methods below
// delegate so template call sites are unchanged.

// quick-select date presets in months (scorecards are pre-calculated monthly
// rather than daily); 12 is the server-side range cap. the toolbar renders
// these as chips plus a Custom option that reveals month-snapped pickers.
const QUICK_RANGE_MONTHS = [1, 3, 6, 12]

// page tabs (fi_tab_bar ids). the active id round-trips through ?tab= so tabs
// deep-link and survive reload; 'fleet' is the default and keeps a clean url.
// FI-2064: Individual Driver and Compare moved out to their own top-level pages
// (driver_manager_individual.vue / driver_manager_compare.vue) under the Driver
// Manager nav section, so only the fleet-wide views remain as tabs here.
const PAGE_TABS = [
  { id: 'fleet', label: 'Roster Overview' },
  { id: 'flagged', label: 'Flagged Drivers' },
  { id: 'trends', label: 'Trends' },
]

export default {
  name: 'driver_manager_dashboard',

  components: { FiTabBar, DriverManagerSettings },

  data() {
    return {
      loading: false,
      error: null,
      drivers: [],
      fleet_average: null,
      scorecard_thresholds: null,
      activeTab: 'fleet',
      customRangeOpen: false,
      settingsModalShown: false,
      settingsInitialTab: 'weights',
      settingsSavedAny: false,
      selected_status_filter: null,
      coachFilters: [],
      coachSearch: '',
      // default window shared with the Coaching Portal's flagged panel via
      // defaultScorecardRange — the two surfaces must flag over the same months
      details_fromDate: justDate(defaultScorecardRange().from),
      details_toDate: justDate(defaultScorecardRange().to),
      chartInstances: {},
      loadingTrends: false,
      trendsError: null,
      trendsMonths: [],
      trendsCacheKey: null,
      trendsDriverId: null,
      // Users can add/remove from the full catalog (below) — this is just the
      // starting point shown before anyone has touched it.
      trendsOpsFields: ['idling_percentage', 'weekend_availability_percentage', 'miles_per_gallon'],
      opsFieldToAdd: '',
    }
  },

  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    mergedThresholds() {
      return mergeThresholds(this.scorecard_thresholds)
    },
    tabDefs() {
      return PAGE_TABS
    },
    canEditSettings() {
      return ['admin', 'customer-admin', 'tbn-admin'].includes(this.user.role)
    },
    isCmTenant() {
      return (this.user && this.user.types && this.user.types[this.user.tenant]) === 'CM'
    },
    totalDrivers() {
      return this.drivers.length
    },
    fleetAvgScore() {
      if (this.fleet_average && this.fleet_average.compiled_score != null) return this.fleet_average.compiled_score
      return this.avg(this.activeDrivers, 'compiled_score')
    },
    tierCounts() {
      const counts = { Elite: 0, Solid: 0, 'Fair': 0, 'High Risk': 0, 'Insufficient Data': 0 }
      this.drivers.forEach(d => {
        if (counts[d.compiled_score_tier] !== undefined) counts[d.compiled_score_tier]++
      })
      return counts
    },
    status_filter_options() {
      const statuses = [...new Set(this.drivers.map(d => d.employment_status).filter(s => s && s.trim()))].sort()
      return [
        { value: null, text: 'All Statuses' },
        ...statuses.map(s => ({ value: s, text: s })),
      ]
    },
    activeDrivers() {
      // Same activity gate used for flags/percentiles: excludes drivers with negligible data in the period.
      return this.drivers.filter(d => this.isActive(d))
    },
    // Pre-sorted ascending value pools per flagged metric, memoized off activeDrivers.
    // metricBadnessPercentile() is called per (driver x metric) while building the
    // Flagged Drivers tab; sorting the whole fleet inside each of those calls made it
    // O(drivers x metrics x N log N) and re-ran on every reactive read. Sorting once
    // here (Vue caches the computed until activeDrivers changes) drops the per-call
    // work to a binary search.
    badnessPools() {
      return buildBadnessPools(this.activeDrivers)
    },
    statusFilteredDrivers() {
      if (!this.selected_status_filter) return this.drivers
      return this.drivers.filter(d => d.employment_status === this.selected_status_filter)
    },
    driverSelectOptions() {
      const sorted = [...this.statusFilteredDrivers].sort((a, b) => (a.driver_name || '').localeCompare(b.driver_name || ''))
      return [
        { value: null, text: 'Select Driver' },
        ...sorted.map(d => ({ value: d.driver_id || d.driver_name, text: d.driver_name })),
      ]
    },
    trendsDriverOptions() {
      return [{ value: null, text: 'All Drivers (Fleet)' }, ...this.driverSelectOptions.slice(1)]
    },
    trendsDriverName() {
      if (!this.trendsDriverId) return ''
      const found = this.trendsDriverOptions.find(o => String(o.value) === String(this.trendsDriverId))
      return found ? found.text : ''
    },
    trendsHasDriverData() {
      return this.trendsMonths.some(m => m.driver)
    },
    // Every catalog entry the user currently has selected (minus anything hidden for
    // this tenant type, e.g. CM tenants never see the TBN-only dispatch fields).
    activeTrendsOpsMetrics() {
      return TREND_OPS_METRICS.filter(m => this.trendsOpsFields.includes(m.key) && (!m.cmHidden || !this.isCmTenant))
    },
    // The "+ Add metric" dropdown's options: whatever isn't already on the chart.
    trendsOpsAvailableMetrics() {
      return TREND_OPS_METRICS.filter(m => !this.trendsOpsFields.includes(m.key) && (!m.cmHidden || !this.isCmTenant))
    },
    trendsOpsChartTitle() {
      const labels = this.activeTrendsOpsMetrics.map(m => m.label).join(', ')
      if (!labels) return this.trendsDriverId ? `${this.trendsDriverName} vs Fleet` : 'Fleet Metrics by Month'
      if (this.trendsDriverId) return `${this.trendsDriverName} vs Fleet — ${labels}`
      return `${labels} by Month`
    },
    scoreRanked() {
      // Insufficient Data drivers are deliberately excluded from ranking — their
      // compiled score isn't a fair signal, so mixing them into "Rank #X of Y" or the
      // Score Distribution chart would be misleading. See isActive()/activeDrivers.
      return [...this.activeDrivers].sort((a, b) => (b.compiled_score || 0) - (a.compiled_score || 0))
    },
    flaggedCounts() {
      const counts = { critical: 0, warning: 0, clean: 0, lowActivity: 0 }
      this.statusFilteredDrivers.forEach(d => {
        counts[this.driverCategory(d)]++
      })
      return counts
    },
    filteredFlaggedList() {
      let data = [...this.statusFilteredDrivers]
      if (this.coachSearch) {
        const q = this.coachSearch.toLowerCase()
        data = data.filter(d => (d.driver_name || '').toLowerCase().includes(q))
      }
      if (this.coachFilters.length) {
        data = data.filter(d => this.coachFilters.includes(this.driverCategory(d)))
      }
      const priority = d => this.criticalFlagCount(d) * 10 + this.getFlags(d).filter(f => f.severity === 'warning').length * 2 + (100 - (d.compiled_score || 0)) / 10
      data.sort((a, b) => priority(b) - priority(a))
      return data
    },
    quickRangeOptions() {
      return QUICK_RANGE_MONTHS
    },
    // Highlights whichever quick-range button (if any) matches the current from/to
    // dates exactly, so manually-picked dates that happen to land on a 1m/3m/6m
    // boundary still light up, and custom ranges show no button as active.
    activeQuickRangeMonths() {
      for (const months of QUICK_RANGE_MONTHS) {
        const { from, to } = this.quickRangeDates(months)
        if (from === this.details_fromDate && to === this.details_toDate) return months
      }
      return null
    },
    // the custom pickers stay revealed while explicitly opened, and reveal
    // themselves when the current range matches no preset (deep link, clamped
    // range) so the active dates are never invisible
    showCustomRange() {
      return this.customRangeOpen || this.activeQuickRangeMonths === null
    },
    // Per-KPI averages across this period's Elite-tier drivers, for the fleet-overview
    // "Fleet Avg vs Elite Avg" radar. null (not zero) when there are no Elite drivers,
    // so the template can show an explanatory message instead of an all-zero chart.
    eliteAverageMetrics() {
      const eliteDrivers = this.drivers.filter(d => d.compiled_score_tier === 'Elite')
      if (!eliteDrivers.length) return null
      const out = {}
      for (const key of RADAR_METRIC_KEYS) {
        const vals = eliteDrivers.map(d => d[key]).filter(v => v !== null && v !== undefined && !isNaN(Number(v)))
        out[key] = vals.length ? vals.reduce((sum, v) => sum + Number(v), 0) / vals.length : null
      }
      return out
    },
    // month-granular data: the pickers only offer first-of-month "from" and
    // last-of-month (or today) "to" dates, same as the comparison's pickers
    optionsFrom() {
      const minD = moment().subtract(3, 'years').startOf('month')
      const maxD = moment()
      return {
        format: 'MM-DD-YYYY', useCurrent: false, widgetPositioning: { horizontal: 'left', vertical: 'bottom' },
        minDate: minD, maxDate: maxD, enabledDates: buildFirstOfMonthDates(minD, maxD),
        keepOpen: false, showClear: false, showClose: true,
      }
    },
    optionsTo() {
      const minD = moment().subtract(3, 'years').startOf('month')
      const maxD = moment()
      return {
        format: 'MM-DD-YYYY', useCurrent: false, widgetPositioning: { horizontal: 'left', vertical: 'bottom' },
        minDate: minD, maxDate: maxD, enabledDates: buildLastOfMonthOrTodayDates(minD, maxD),
        keepOpen: false, showClear: false, showClose: true,
      }
    },
  },

  watch: {
    activeTab(id) {
      this.syncTabQuery(id)
      this.$nextTick(() => {
        if (id === 'fleet') this.buildFleetCharts()
        if (id === 'trends') this.loadTrendsIfNeeded()
      })
    },
    // back/forward navigation between tab states
    '$route.query.tab'() {
      this.hydrateTabFromRoute()
    },
    '$route.query.settings'() {
      this.syncSettingsModal()
    },
    trendsDriverId() {
      this.trendsCacheKey = null
      this.loadTrendsIfNeeded()
    },
    trendsOpsFields() {
      this.$nextTick(() => this.buildTrendsCharts())
    },
    // both date watchers end by scheduling a reload; corrective re-assignments
    // retrigger the watcher, and scheduleRangeLoad coalesces the cascade into
    // one load_data per user action. an unparseable value (cleared/typed-out
    // field) bails outright so "Invalid date" can never snap-loop or reach the
    // api as a month string
    details_toDate() {
      const today = moment()
      const to = moment(this.details_toDate, 'MM-DD-YYYY')
      if (!to.isValid()) return
      if (to.isAfter(today)) { this.details_toDate = justDate(today); return }
      // month-granular data: snap a mid-month pick to the month end (today is
      // allowed within the current month, matching the picker's enabledDates)
      if (!to.isSame(moment(to).endOf('month'), 'day') && !to.isSame(today, 'day')) {
        this.details_toDate = justDate(moment.min(moment(to).endOf('month'), today))
        return
      }
      if (moment(this.details_fromDate, 'MM-DD-YYYY') > to) {
        this.details_fromDate = justDate(moment(to).startOf('month'))
      }
      const months = to.diff(moment(this.details_fromDate, 'MM-DD-YYYY'), 'months', true)
      if (months > 12) this.details_fromDate = moment(to).subtract(12, 'months').startOf('month').format('MM-DD-YYYY')
      this.scheduleRangeLoad()
    },
    details_fromDate() {
      const from = moment(this.details_fromDate, 'MM-DD-YYYY')
      if (!from.isValid()) return
      if (!from.isSame(moment(from).startOf('month'), 'day')) {
        this.details_fromDate = justDate(moment(from).startOf('month'))
        return
      }
      if (from > moment(this.details_toDate, 'MM-DD-YYYY')) {
        this.details_toDate = justDate(moment.min(moment(from).endOf('month'), moment()))
      }
      const months = moment(this.details_toDate, 'MM-DD-YYYY').diff(from, 'months', true)
      if (months > 12) this.details_toDate = moment(from).add(12, 'months').endOf('month').format('MM-DD-YYYY')
      this.scheduleRangeLoad()
    },
  },

  created() {
    this.hydrateTabFromRoute()
    this.load_data()
  },

  mounted() {
    // deep link ?settings=1 (b-modal needs the rendered tree, so not created())
    this.syncSettingsModal()
  },

  beforeDestroy() {
    Object.values(this.chartInstances).forEach(c => c && c.destroy())
  },

  methods: {
    // Trends tab "Ops" chart field picker (FI-2018 follow-up).
    addOpsField(key) {
      if (key && !this.trendsOpsFields.includes(key)) this.trendsOpsFields.push(key)
      this.opsFieldToAdd = ''
    },
    removeOpsField(key) {
      this.trendsOpsFields = this.trendsOpsFields.filter(k => k !== key)
    },

    async load_data() {
      // sequence guard: with auto-refresh a slow response can arrive after a
      // newer request; only the latest request may write state
      const seq = (this._loadSeq = (this._loadSeq || 0) + 1)
      this.loading = true
      this.error = null
      try {
        const start_month = moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM')
        const end_month = moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM')
        const response = this.isCmTenant
          ? await cm_get_all_driver_scorecards(this.user.tenant, start_month, end_month)
          : await get_all_driver_scorecards(this.user.tenant, start_month, end_month)
        if (seq !== this._loadSeq) return
        const responseData = (response && response.value && response.value.data) || (response && response.data)
        if (responseData && responseData.error) {
          this.error = responseData.error
          this.drivers = []
        } else if (responseData) {
          this.drivers = (responseData.drivers || []).map(d => this.normalizeDriverRow(d))
          this.fleet_average = this.normalizeDriverRow(responseData.fleet_average) || null
          this.scorecard_thresholds = responseData.scorecard_thresholds !== undefined ? responseData.scorecard_thresholds : null
        } else {
          this.error = 'Unexpected response format from server'
        }
      } catch (e) {
        if (seq !== this._loadSeq) return
        const serverMsg = (e.response && e.response.data && e.response.data.error)
          || (e.response && e.response.data && e.response.data.data && e.response.data.data.error)
        this.error = serverMsg || e.message || 'Error loading fleet driver data'
      } finally {
        if (seq === this._loadSeq) {
          this.loading = false
          this.trendsCacheKey = null
          this.$nextTick(() => {
            if (this.activeTab === 'fleet') this.buildFleetCharts()
            if (this.activeTab === 'trends') this.loadTrendsIfNeeded()
          })
        }
      }
    },

    async loadTrendsIfNeeded() {
      // while the main fleet load is in flight, skip -- its finally re-invokes
      // this with settled dates, so an early fire (e.g. the ?tab=trends deep
      // link's watcher) would only duplicate the request
      if (this.loading) return
      const start_month = moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM')
      const end_month = moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM')
      const key = `${start_month}|${end_month}|${this.trendsDriverId || ''}`
      if (this.trendsCacheKey === key) {
        this.$nextTick(() => this.buildTrendsCharts())
        return
      }
      // same sequence guard as load_data: only the latest request writes state
      const seq = (this._trendsSeq = (this._trendsSeq || 0) + 1)
      this.loadingTrends = true
      this.trendsError = null
      try {
        const response = this.isCmTenant
          ? await cm_get_driver_scorecard_trends(this.user.tenant, start_month, end_month, this.trendsDriverId)
          : await get_driver_scorecard_trends(this.user.tenant, start_month, end_month, this.trendsDriverId)
        if (seq !== this._trendsSeq) return
        const responseData = (response && response.value && response.value.data) || (response && response.data)
        if (responseData && responseData.error) {
          this.trendsError = responseData.error
          this.trendsMonths = []
        } else if (responseData) {
          this.trendsMonths = responseData.months || []
          this.trendsCacheKey = key
        } else {
          this.trendsError = 'Unexpected response format from server'
        }
      } catch (e) {
        if (seq !== this._trendsSeq) return
        const serverMsg = (e.response && e.response.data && e.response.data.error)
          || (e.response && e.response.data && e.response.data.data && e.response.data.data.error)
        this.trendsError = serverMsg || e.message || 'Error loading trend data'
      } finally {
        if (seq === this._trendsSeq) {
          this.loadingTrends = false
          this.$nextTick(() => this.buildTrendsCharts())
        }
      }
    },

    // "Last full month" mirrors the same >=7th-of-month cutover the default range
    // uses: early in a month, the just-finished month's pre-calc may not have landed
    // yet, so roll back one extra month rather than land on an empty period.
    _lastFullMonthEnd() {
      return moment().date() >= 7 ? moment().subtract(1, 'month') : moment().subtract(2, 'months')
    },
    quickRangeDates(months) {
      const to = this._lastFullMonthEnd().endOf('month')
      const from = to.clone().subtract(months - 1, 'months').startOf('month')
      return { from: justDate(from), to: justDate(to) }
    },
    setDatePreset(preset) {
      if (preset === 'custom') {
        this.customRangeOpen = true
        return
      }
      this.customRangeOpen = false
      const { from, to } = this.quickRangeDates(preset)
      // assigning triggers the date watchers, which schedule the reload;
      // unchanged dates fire nothing, so re-clicking the active chip is free
      this.details_fromDate = from
      this.details_toDate = to
    },
    // coalesces the watcher cascade (snap, cross-field clamp) into a single
    // reload per user action
    scheduleRangeLoad() {
      if (this._rangeLoadScheduled) return
      this._rangeLoadScheduled = true
      this.$nextTick(() => {
        this._rangeLoadScheduled = false
        this.load_data()
      })
    },

    // Individual Driver moved to its own top-level page (FI-2064) -- deep-links
    // there with the driver preselected via ?driver_id= instead of switching tabs.
    openDriver(driverId) {
      this.$router.push({ name: 'driver_manager_individual', query: { driver_id: driverId } })
    },

    // tab is optional ('coloring' opens that tab); guard against the raw
    // MouseEvent that a bare @click="openSettings" binding passes
    openSettings(tab) {
      if (!this.canEditSettings) return
      const tabId = tab === 'coloring' ? 'coloring' : 'weights'
      this.settingsInitialTab = tabId
      const query = { ...this.$route.query, settings: '1' }
      if (tabId === 'coloring') query.settings_tab = 'coloring'
      else delete query.settings_tab
      this.$router.replace({ query }).catch(() => {})
      this.$bvModal.show('dsd-settings-modal')
    },
    syncSettingsModal() {
      const wants = this.$route.query.settings === '1'
      // strip the flag for viewers who can't edit settings (deep link handed around)
      if (wants && !this.canEditSettings) {
        const query = { ...this.$route.query }
        delete query.settings
        delete query.settings_tab
        this.$router.replace({ query }).catch(() => {})
        return
      }
      if (wants) {
        this.settingsInitialTab = this.$route.query.settings_tab === 'coloring' ? 'coloring' : 'weights'
        this.$bvModal.show('dsd-settings-modal')
      } else if (this.settingsModalShown) {
        this.$bvModal.hide('dsd-settings-modal')
      }
    },
    onSettingsHidden() {
      this.settingsModalShown = false
      const query = { ...this.$route.query }
      delete query.settings
      delete query.settings_tab
      this.$router.replace({ query }).catch(() => {})
      if (this.settingsSavedAny) {
        this.settingsSavedAny = false
        // weights and thresholds change server-computed scores and band coloring
        this.load_data()
      }
    },

    syncTabQuery(id) {
      const current = this.$route.query.tab || 'fleet'
      if (current === id) return
      const query = { ...this.$route.query }
      if (id === 'fleet') delete query.tab
      else query.tab = id
      this.$router.replace({ query }).catch(() => {})
    },
    hydrateTabFromRoute() {
      const id = this.$route.query.tab || 'fleet'
      if (PAGE_TABS.some(t => t.id === id) && id !== this.activeTab) this.activeTab = id
    },

    isActive(d) {
      return isActiveDriver(d)
    },

    avg(arr, key) {
      const vals = arr.map(d => d[key]).filter(v => v != null && typeof v === 'number')
      return vals.length ? this.round2(vals.reduce((a, b) => a + b, 0) / vals.length) : null
    },

    round2(value) {
      if (value === null || value === undefined || isNaN(Number(value))) return value
      return Math.round(Number(value) * 100) / 100
    },

    // CM has no dispatch-route-mileage field like TBN's total_miles — it only has
    // Samsara telematics odometer mileage (miles_driven). Map that onto total_miles
    // so every other total_miles reference in this component (table, KPIs, charts)
    // works unmodified for both tenant types.
    normalizeDriverRow(row) {
      if (!row) return row
      if (this.isCmTenant && row.total_miles == null && row.miles_driven != null) {
        return { ...row, total_miles: row.miles_driven }
      }
      return row
    },

    tierPct(tier) {
      if (!this.totalDrivers) return '0.00'
      return ((this.tierCounts[tier] / this.totalDrivers) * 100).toFixed(2)
    },

    getCompiledScoreClass(score, tier) {
      return getCompiledScoreClass(score, this.mergedThresholds.compiled_score, tier)
    },

    getCompiledScoreTextClass(score, tier) {
      const cls = this.getCompiledScoreClass(score, tier)
      const map = {
        'compiled-score-elite': 'sc-tier-text--elite',
        'compiled-score-solid': 'sc-tier-text--solid',
        'compiled-score-coaching': 'sc-tier-text--coaching',
        'compiled-score-highrisk': 'sc-tier-text--highrisk',
      }
      return map[cls] || ''
    },

    getCompiledTierBadgeClass(tier) {
      return tierClass(tier, 'chip')
    },

    initials(name) {
      if (!name) return '?'
      return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
    },

    rankOf(d) {
      const idx = this.scoreRanked.findIndex(x => x === d)
      return idx === -1 ? null : idx + 1
    },

    // Where a driver's value on `dataKey` ranks among the active fleet — see
    // utils/scorecard_flags.js for the full contract. Kept as a method so
    // existing call sites and unit tests keep working against the component.
    metricBadnessPercentile(d, dataKey, higherBetter) {
      return metricBadnessPercentile(d, dataKey, higherBetter, this.badnessPools, this.activeDrivers)
    },

    getFlags(d) {
      return getDriverFlags(d, {
        pools: this.badnessPools,
        activeDrivers: this.activeDrivers,
        thresholds: this.mergedThresholds,
      })
    },

    criticalFlagCount(d) {
      return this.getFlags(d).filter(f => f.severity === 'critical').length
    },

    // classifies a driver into exactly one bucket, shared by the chip counts,
    // the chip filter, and the card's left-border accent so all three always agree
    driverCategory(d) {
      if (!this.isActive(d)) return 'lowActivity'
      if (this.criticalFlagCount(d) > 0) return 'critical'
      if (this.getFlags(d).some(f => f.severity === 'warning')) return 'warning'
      return 'clean'
    },

    toggleCoachFilter(key) {
      const idx = this.coachFilters.indexOf(key)
      if (idx === -1) this.coachFilters.push(key)
      else this.coachFilters.splice(idx, 1)
    },

    clearCoachFilters() {
      this.coachFilters = []
      this.coachSearch = ''
    },

    coachCardClass(d) {
      const map = {
        critical: 'dsd-has-critical',
        warning: 'dsd-has-warning',
        clean: 'dsd-clean',
        lowActivity: 'dsd-has-lowactivity',
      }
      return map[this.driverCategory(d)]
    },

    formatNumber(value, decimals = 2) {
      if (value === null || value === undefined || isNaN(Number(value))) return '—'
      const fixed = Number(value).toFixed(decimals)
      // A value that rounds to a whole number ("85.00") shows as "85" -- the
      // trailing zeros are just noise. A genuinely fractional value ("85.32")
      // keeps its decimals.
      const allZeroDecimals = decimals > 0 && fixed.endsWith('.' + '0'.repeat(decimals))
      return allZeroDecimals ? fixed.slice(0, -(decimals + 1)) : fixed
    },

    buildFleetCharts() {
      if (!this.$refs.chScores || !this.drivers.length) return
      try {
        this.buildFleetChartsInner()
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[driver_manager_dashboard] failed to build fleet charts', e)
      }
    },

    buildFleetChartsInner() {
      this.applyChartTheme()
      // Insufficient Data drivers aren't fairly rankable — leave them out of the score
      // distribution so they don't clutter the chart with unranked, misleading bars.
      const sorted = this.scoreRanked
      this.destroyChart('scores')
      this.chartInstances.scores = new Chart(this.freshCanvasCtx(this.$refs.chScores), {
        type: 'bar',
        data: {
          labels: sorted.map(d => this.surnameOf(d.driver_name)),
          datasets: [{
            data: sorted.map(d => this.round2(d.compiled_score)),
            backgroundColor: sorted.map(d => this.tc(d.compiled_score_tier)),
            borderRadius: 3,
          }],
        },
        options: {
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: { x: { ticks: { font: { size: 8 } } }, y: { min: 0, max: 100 } },
        },
      })

      this.destroyChart('tiers')
      this.chartInstances.tiers = new Chart(this.freshCanvasCtx(this.$refs.chTiers), {
        type: 'doughnut',
        data: {
          labels: CHART_TIER_ORDER,
          datasets: [{
            data: CHART_TIER_ORDER.map(t => this.tierCounts[t]),
            backgroundColor: CHART_TIER_ORDER.map(t => this.tc(t)),
          }],
        },
        options: {
          maintainAspectRatio: false,
          cutout: '65%',
          plugins: { legend: { position: 'bottom' } },
          onClick: (evt, elements) => {
            if (!elements.length) return
            const tier = CHART_TIER_ORDER[elements[0].index]
            // The leaderboard table moved to its own Roster List page — a slice
            // click now deep-links there with the tier chip pre-selected.
            this.$router.push({ name: 'driver_manager_roster_list', query: { tier } })
          },
          onHover: (evt, elements) => {
            evt.native.target.style.cursor = elements.length ? 'pointer' : 'default'
          },
        },
      })

      // "Am I underutilizing my best drivers, or putting my riskiest drivers on the road
      // more than others?" — share of total miles driven, grouped by compiled-score tier.
      const milesByTier = CHART_TIER_ORDER.map(t =>
        this.drivers.filter(d => d.compiled_score_tier === t)
          .reduce((sum, d) => sum + (Number(d.total_miles) || 0), 0)
      )
      const totalMiles = milesByTier.reduce((a, b) => a + b, 0)
      const milesPct = milesByTier.map(m => totalMiles > 0 ? Math.round((m / totalMiles) * 1000) / 10 : 0)
      this.destroyChart('kpis')
      this.chartInstances.kpis = new Chart(this.freshCanvasCtx(this.$refs.chKpis), {
        type: 'bar',
        data: {
          labels: CHART_TIER_ORDER,
          datasets: [{
            data: milesPct,
            backgroundColor: CHART_TIER_ORDER.map(t => this.tc(t)),
            borderRadius: 4,
          }],
        },
        options: {
          maintainAspectRatio: false,
          indexAxis: 'y',
          plugins: {
            legend: { display: false },
            tooltip: { callbacks: { label: ctx => `${ctx.parsed.x}% of miles driven` } },
          },
          scales: { x: { min: 0, max: 100, ticks: { callback: v => v + '%' } } },
        },
      })

      this.destroyChart('fleetRadar')
      const elite = this.eliteAverageMetrics
      if (elite && this.$refs.chFleetRadar) {
        const fa = this.fleet_average || {}
        this.chartInstances.fleetRadar = new Chart(this.freshCanvasCtx(this.$refs.chFleetRadar), {
          type: 'radar',
          data: {
            labels: RADAR_LABELS,
            datasets: [
              {
                label: 'Elite Avg',
                data: this.radarMetricValues(elite),
                backgroundColor: this.tc('Elite') + '33', borderColor: this.tc('Elite'), pointBackgroundColor: this.tc('Elite'), borderWidth: 2,
              },
              {
                label: 'Fleet Avg',
                data: this.radarMetricValues(fa),
                backgroundColor: 'rgba(108,117,125,.12)', borderColor: '#6c757d', pointBackgroundColor: '#6c757d',
                borderWidth: 1.5, borderDash: [4, 4],
              },
            ],
          },
          options: { maintainAspectRatio: false, scales: { r: { min: 0, max: 100, ticks: { display: false } } }, plugins: { legend: { position: 'bottom' } } },
        })
      }
    },

    // Shared by the individual-driver radar (driver_manager_individual.vue, self vs
    // fleet avg) and the fleet-overview radar here (fleet avg vs elite avg) — six
    // 0-100 "goodness" scores derived from the same raw KPIs, so both radars read on
    // the same axes/labels. Kept in sync manually with the individual page's copy.
    radarMetricValues(row) {
      const r = row || {}
      const rn = v => Math.max(0, 100 - ((v || 0) / 3) * 100)
      const r2 = this.round2
      return [
        r2(r.risk_score),
        r2(rn(r.speeding_percentage)),
        r2(Math.max(0, 100 - ((r.distracted_driving_per_10k_miles || 0) / 15) * 100)),
        r2(Math.max(0, 100 - ((r.following_distance_per_10k_miles || 0) / 10) * 100)),
        r2(Math.max(0, 100 - ((r.idling_percentage || 0) / 60) * 100)),
        r2(Math.min(((r.miles_per_gallon || 0) / 10) * 100, 100)),
      ]
    },

    // Bar borders in Chart.js are filled shapes, not stroked lines, so dashed borders
    // don't render on bars — a diagonal-stripe canvas pattern is the reliable way to make
    // a single stacked segment visually pop as "highlighted".
    stripePattern(baseColor) {
      const size = 10
      const canvas = document.createElement('canvas')
      canvas.width = size
      canvas.height = size
      const c = canvas.getContext('2d')
      c.fillStyle = baseColor
      c.fillRect(0, 0, size, size)
      c.strokeStyle = 'rgba(255, 255, 255, 0.85)'
      c.lineWidth = 3
      c.beginPath()
      c.moveTo(0, size)
      c.lineTo(size, 0)
      c.moveTo(-size / 2, size / 2)
      c.lineTo(size / 2, -size / 2)
      c.moveTo(size / 2, size * 1.5)
      c.lineTo(size * 1.5, size / 2)
      c.stroke()
      return c.createPattern(canvas, 'repeat')
    },

    buildTrendsCharts() {
      if (!this.$refs.chTrendScore || !this.trendsMonths.length) return
      try {
        this.buildTrendsChartsInner()
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[driver_manager_dashboard] failed to build trend charts', e)
      }
    },

    buildTrendsChartsInner() {
      this.applyChartTheme()
      const months = this.trendsMonths
      const labels = months.map(m => moment(m.month, 'YYYY-MM').format('MMM YY'))
      const r2 = this.round2
      const avgField = (f, mult = 1) => months.map(m => (m.fleet_average && m.fleet_average[f] != null) ? r2(m.fleet_average[f] * mult) : null)
      const driverField = (f, mult = 1) => months.map(m => (m.driver && m.driver[f] != null) ? r2(m.driver[f] * mult) : null)
      const showDriver = !!this.trendsDriverId && this.trendsHasDriverData
      const driverName = this.trendsDriverName || 'Driver'

      this.destroyChart('trendScore')
      this.chartInstances.trendScore = new Chart(this.freshCanvasCtx(this.$refs.chTrendScore), {
        type: 'line',
        data: {
          labels,
          datasets: [
            { label: 'Fleet Avg Combined Score', data: avgField('compiled_score'), borderColor: this.tc('Elite'), backgroundColor: this.tc('Elite'), tension: 0.3 },
            { label: 'Fleet Safety Score', data: avgField('risk_score'), borderColor: this.tc('Solid'), backgroundColor: this.tc('Solid'), tension: 0.3 },
            ...(showDriver ? [
              { label: `${driverName} Combined Score`, data: driverField('compiled_score'), borderColor: this.tc('Elite'), backgroundColor: this.tc('Elite'), borderDash: [6, 4], borderWidth: 3, tension: 0.3 },
              { label: `${driverName} Safety`, data: driverField('risk_score'), borderColor: this.tc('Solid'), backgroundColor: this.tc('Solid'), borderDash: [6, 4], borderWidth: 3, tension: 0.3 },
            ] : []),
          ],
        },
        options: { maintainAspectRatio: false, scales: { y: { min: 0, max: 100 } }, plugins: { legend: { position: 'bottom' } } },
      })

      // Highlight (not a trend line, which reads oddly against a headcount axis) the
      // segment of the stacked bar that the selected driver actually landed in that month.
      // Chart.js draws bar borders as a filled ring shape rather than a stroked path, so
      // borderDash has no visual effect on bars at all — a diagonal-stripe pattern fill is
      // the reliable way to make one segment visually pop.
      const isDriverTierThisMonth = (m, t) => showDriver && m.driver && m.driver.compiled_score_tier === t
      const highlightPatterns = {}
      CHART_TIER_ORDER.forEach(t => { highlightPatterns[t] = this.stripePattern(this.tc(t)) })
      const legendPattern = this.stripePattern('#6c757d')

      this.destroyChart('trendTiers')
      this.chartInstances.trendTiers = new Chart(this.freshCanvasCtx(this.$refs.chTrendTiers), {
        type: 'bar',
        data: {
          labels,
          datasets: [
            ...CHART_TIER_ORDER.map(t => ({
              label: t,
              backgroundColor: months.map(m => isDriverTierThisMonth(m, t) ? highlightPatterns[t] : this.tc(t)),
              data: months.map(m => (m.tier_counts && m.tier_counts[t]) || 0),
              borderColor: '#212529',
              borderWidth: months.map(m => isDriverTierThisMonth(m, t) ? 2 : 0),
            })),
            // Zero-value dataset purely so the highlight style gets its own legend key —
            // it draws nothing (0 height everywhere) but shows the striped swatch.
            ...(showDriver ? [{
              label: `${driverName}'s Tier`,
              backgroundColor: legendPattern,
              borderColor: '#212529',
              borderWidth: 2,
              data: months.map(() => 0),
            }] : []),
          ],
        },
        options: {
          maintainAspectRatio: false,
          scales: { x: { stacked: true }, y: { stacked: true, ticks: { precision: 0 } } },
          plugins: {
            legend: {
              position: 'bottom',
              labels: {
                // Chart.js's default legend swatch picks backgroundColor[0] when the
                // dataset's backgroundColor is a per-month array — since the highlighted
                // month isn't necessarily month 0, that made whichever tier happened to be
                // highlighted in month 0 show a striped legend key. Force each tier's own
                // flat color instead of letting it inherit an arbitrary indexed value.
                // fontColor must be set explicitly here: Chart.js's legend renderer reads
                // legendItem.fontColor directly with no fallback (chart.js:8588-8589), and
                // only the *default* generateLabels fills that in from the theme color —
                // a custom generateLabels like this one that omits it leaves canvas fillStyle
                // at whatever it was last set to, which reads as invisible text in midnight
                // mode (dark text/leftover color on a dark background).
                generateLabels: chart => chart.data.datasets.map((ds, i) => ({
                  text: ds.label,
                  fillStyle: Array.isArray(ds.backgroundColor) ? this.tc(ds.label) : ds.backgroundColor,
                  strokeStyle: ds.borderColor,
                  fontColor: Chart.defaults.color,
                  lineWidth: 1,
                  hidden: !chart.isDatasetVisible(i),
                  datasetIndex: i,
                })),
              },
            },
            tooltip: {
              filter: item => CHART_TIER_ORDER.includes(item.dataset.label),
              callbacks: {
                label: context => {
                  const isDriverMonth = isDriverTierThisMonth(months[context.dataIndex], context.dataset.label)
                  const suffix = isDriverMonth ? ` (${driverName})` : ''
                  return `${context.dataset.label}: ${context.parsed.y}${suffix}`
                },
              },
            },
          },
        },
      })

      this.destroyChart('trendOps')
      // The canvas itself is v-if'd away (replaced by a "pick a metric" placeholder)
      // when the user has cleared every field from the chart.
      const opsMetrics = this.activeTrendsOpsMetrics
      if (this.$refs.chTrendOps && opsMetrics.length) {
        // Once every selected metric is a 0-100 percentage, the fixed ceiling makes it
        // easy to compare lines at a glance; the moment a rate-style field (e.g. harsh
        // braking/10k mi) joins the mix, a 100 ceiling would flatten it against the
        // bottom of the chart, so fall back to an auto-scaled axis instead.
        const allPercent = opsMetrics.every(m => m.percent)
        this.chartInstances.trendOps = new Chart(this.freshCanvasCtx(this.$refs.chTrendOps), {
          type: 'line',
          data: {
            labels,
            // _scale/_rawLabel are plain extra props Chart.js ignores for rendering but
            // passes through on context.dataset — used by the tooltip callback below to
            // un-scale the displayed value and drop the "(x10)" suffix from the hover text.
            datasets: [
              ...opsMetrics.map(m => ({
                label: m.scale ? `Fleet ${m.label} (×${m.scale})` : `Fleet ${m.label}`,
                data: avgField(m.key, m.scale || 1),
                borderColor: this.opsColor(m), backgroundColor: this.opsColor(m), tension: 0.3,
                _scale: m.scale || 1, _rawLabel: `Fleet ${m.label}`,
              })),
              ...(showDriver ? opsMetrics.map(m => ({
                label: m.scale ? `${driverName} ${m.label} (×${m.scale})` : `${driverName} ${m.label}`,
                data: driverField(m.key, m.scale || 1),
                borderColor: this.opsColor(m), backgroundColor: this.opsColor(m), borderDash: [6, 4], borderWidth: 3, tension: 0.3,
                _scale: m.scale || 1, _rawLabel: `${driverName} ${m.label}`,
              })) : []),
            ],
          },
          options: {
            maintainAspectRatio: false,
            scales: { y: allPercent ? { min: 0, max: 100 } : { min: 0 } },
            plugins: {
              legend: { position: 'bottom' },
              tooltip: {
                callbacks: {
                  label: context => {
                    const ds = context.dataset
                    const scale = ds._scale || 1
                    const value = scale !== 1 ? r2(context.parsed.y / scale) : context.parsed.y
                    return `${ds._rawLabel || ds.label}: ${value}`
                  },
                },
              },
            },
          },
        })
      }
    },

    // FI-1871: chart.js renders tick labels, legends, gridlines and radar axes with its
    // light-theme defaults (#666 ink, near-black gridlines) which wash out on the midnight
    // canvas. retint those structural colors to the active site theme before building; the
    // data series colors (tier greens/blues, teal, purple) stay saturated and read on either.
    applyChartTheme() {
      const midnight = document.documentElement.getAttribute('data-fi-theme') === 'midnight'
      this._midnight = midnight
      // chart furniture reads the live tokens so it tracks the palette; the
      // hardcoded values remain as fallbacks for anything outside the shell
      const rootStyles = getComputedStyle(document.documentElement)
      const mutedInk = rootStyles.getPropertyValue('--fi-text-muted').trim()
      const gridLine = rootStyles.getPropertyValue('--fi-line').trim()
      Chart.defaults.color = mutedInk || (midnight ? '#c7d2dd' : '#666')
      Chart.defaults.borderColor = gridLine || (midnight ? 'rgba(255, 255, 255, 0.09)' : 'rgba(0, 0, 0, 0.1)')
      Chart.defaults.font.family = getComputedStyle(document.body).fontFamily || Chart.defaults.font.family
      // Doughnut/pie arcs default to a 2px white separator between slices, which reads as
      // jarring bright gaps on the dark midnight card. Match the separator to the card
      // surface (var(--fi-surface), read live so it tracks the palette) so slices stay
      // cleanly divided without the white ring; light theme keeps the default white.
      let arcBorder = '#fff'
      if (midnight) {
        const surface = getComputedStyle(document.documentElement)
          .getPropertyValue('--fi-surface').trim()
        arcBorder = surface || '#1b2430'
      }
      Chart.defaults.elements.arc.borderColor = arcBorder
    },

    // theme-resolved tier hue for chart series (applyChartTheme() runs at the
    // top of every builder, so _midnight is always current)
    tc(tier) {
      return getTierColor(tier, this._midnight ? 'midnight' : 'light')
    },
    // theme-resolved hue for a TREND_OPS_METRICS entry
    opsColor(m) {
      return this._midnight && m.colorDark ? m.colorDark : m.color
    },

    // surname for compact chart labels: "LAST, FIRST" -> LAST; falls back to
    // the last whitespace token for verbatim "First Last" style names (cm
    // tenants, or tbn rows read before the canonical-name join deployed)
    surnameOf(name) {
      const n = (name || '').trim()
      if (!n) return ''
      const comma = n.indexOf(',')
      if (comma > 0) return n.slice(0, comma).trim()
      return n.split(' ').pop()
    },

    destroyChart(key) {
      if (this.chartInstances[key]) {
        this.chartInstances[key].destroy()
        this.chartInstances[key] = null
      }
    },

    // Safety net for HMR/dev-reload: if a prior Chart instance is still attached to this
    // canvas (e.g. this.chartInstances lost its reference across a hot-reload), Chart.js throws
    // "Canvas is already in use" and refuses to render anything on it.
    freshCanvasCtx(canvasEl) {
      const existing = Chart.getChart(canvasEl)
      if (existing) existing.destroy()
      return canvasEl.getContext('2d')
    },
  },
}
</script>

<style scoped>
/* date preset chips (od-chip recipe, token-native so midnight needs no
   overrides) + the custom-range reveal driven by .sc-toolbar--custom */
.dsd-date-presets {
  gap: 6px;
}
.sc-chip {
  background: var(--fi-surface, #f8fafc);
  border: 1px solid var(--fi-line, #d6dde6);
  border-radius: var(--fi-radius-pill, 999px);
  padding: 4px 12px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--fi-text-muted, #4b5563);
  cursor: pointer;
  transition: background .12s, border-color .12s, color .12s;
}
.sc-chip:hover {
  background: var(--fi-surface-sunken, #eef4fb);
}
.sc-chip--active {
  background: var(--fi-ops-accent, #3a6e99);
  border-color: var(--fi-ops-accent, #3a6e99);
  color: #fff;
}
.dsd-date-fields {
  display: none;
}
.sc-toolbar--custom .dsd-date-fields {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.dsd-date-field {
  width: 122px;
  font-size: 0.9rem;
}
/* ios safari zooms the page on focusing any input under 16px */
@media (max-width: 768px) {
  .dsd-date-field {
    font-size: 16px;
  }
}
.dsd-date-sep,
.dsd-toolbar-label {
  color: var(--fi-text-muted, #6c757d);
  font-size: 0.85rem;
}
/* reloads keep the previous render visible at reduced opacity instead of
   flashing back to a spinner (stale-hold, ops_desk_analytics precedent) */
.dsd-stale {
  opacity: 0.55;
  transition: opacity 0.15s;
}

.scorecard-metrics-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.6rem;
  margin-bottom: 1.5rem;
}

.metric-card-inline {
  flex: 0 0 auto;
  min-width: 128px;
  padding: 0.6rem 0.7rem !important;
  margin-bottom: 0 !important;
  text-align: center;
}
.metric-card-inline h5 {
  font-size: 0.8rem;
  margin-bottom: 0.25rem;
  line-height: 1.15;
  color: #545454;
}
.metric-card-inline h2 {
  font-size: 1.35rem;
  margin-bottom: 0.1rem;
}
.metric-card-inline h3 {
  font-size: 1.15rem;
  margin-bottom: 0.1rem;
}
.metric-card-inline span {
  font-size: 0.7rem !important;
}

/* Fleet Overview's row has 7 tiles (Total Drivers, Fleet Avg, and 5 tiers) — too many
   to stay slim enough at 108px/tile on a laptop-width screen, so it gets its own
   narrower sizing instead of sharing the more spacious default above. */
.dsd-metrics-row.scorecard-metrics-row {
  gap: 0.5rem;
}
.dsd-metrics-row .metric-card-inline {
  min-width: 108px;
  padding: 0.5rem 0.5rem !important;
}
.dsd-metrics-row .metric-card-inline h5 {
  font-size: 0.72rem;
}
.dsd-metrics-row .metric-card-inline h2 {
  font-size: 1.15rem;
}

/* tier inks/chips/fills/tints live in the shared .sc-tier-* family
   (assets/css/driver-manager-mobile.css) via tierClass() */
.text-insufficient { color: #6c757d; }

/* Trends tab "Ops" chart field picker (FI-2018 follow-up) */
.dsd-ops-field-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin: 6px 0 10px;
}
.dsd-ops-field-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #f1f3f5;
  border-radius: 4px;
  padding: 3px 6px 3px 10px;
  font-size: 12px;
  color: #555;
}
.dsd-ops-field-remove {
  background: none;
  border: none;
  padding: 0 2px;
  line-height: 1;
  font-size: 14px;
  color: #999;
  cursor: pointer;
}
.dsd-ops-field-remove:hover { color: #dc3545; }
.dsd-ops-field-add {
  width: auto;
  max-width: 200px;
  font-size: 12px;
  padding: 2px 24px 2px 8px;
  height: auto;
}

.dsd-chart-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  gap: 20px;
  margin-top: 20px;
}
.ad-chart-card {
  border: solid 1px var(--fi-line, #ddd);
  background: var(--fi-surface, #fff);
  border-radius: 12px;
  padding: 15px;
}
.ad-chart-card--wide {
  grid-column: 1 / -1;
}
.ad-chart-title {
  text-align: center;
  font-weight: bold;
  font-size: 1rem;
  color: var(--fi-text, #545454);
  margin-bottom: 10px;
}
.ad-chart-canvas-wrap {
  position: relative;
  width: 100%;
}

.dsd-chip-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin: 10px;
}

.dsd-chip {
  border: 2px solid transparent;
  border-radius: 8px;
  padding: 10px 18px;
  font-weight: bold;
  font-size: 18px;
  cursor: pointer;
  transition: transform 0.1s, box-shadow 0.15s;
}
.dsd-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}
.dsd-chip-count {
  font-size: 1.1em;
}

/* Same "choose one" treatment as the flag items below: a tinted background is
   enough to scan counts at a glance without also coloring the text and border
   on top of it. Text stays the default ink; border stays a plain neutral. */
.dsd-chip-crit  { background: #fdecea; }
.dsd-chip-warn  { background: #fff9e6; }
.dsd-chip-clean { background: #eaf7ec; }
.dsd-chip-low   { background: #f4f5f6; }

.dsd-chip-crit.active  { border-color: #dc3545; box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.25); }
.dsd-chip-warn.active  { border-color: #d39e00; box-shadow: 0 0 0 2px rgba(211, 158, 0, 0.25); }
.dsd-chip-clean.active { border-color: #28a745; box-shadow: 0 0 0 2px rgba(40, 167, 69, 0.25); }
.dsd-chip-low.active   { border-color: #6c757d; box-shadow: 0 0 0 2px rgba(108, 117, 125, 0.25); }

.dsd-coach-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}
.dsd-coach-card {
  border-left: 4px solid #ced4da;
}
.dsd-coach-card.dsd-has-critical { border-left-color: #dc3545; }
.dsd-coach-card.dsd-has-warning { border-left-color: #ffc107; }
.dsd-coach-card.dsd-clean { border-left-color: #28a745; }
.dsd-coach-card.dsd-has-lowactivity { border-left-color: #adb5bd; }

/* Severity is color-coded on the trim (left border) only — the card already
   carries the same-colored left border, and stacking a tinted background AND
   colored label text on top of that reads as too aggressive/"in your face".
   Background stays neutral; the emoji + label text still convey severity. */
.dsd-flag-item {
  padding: 7px 10px;
  border-radius: 4px;
  margin-bottom: 8px;
  background: #f8f9fa;
  border-left: 3px solid transparent;
}
.dsd-flag-critical { border-left-color: #dc3545; }
.dsd-flag-warning { border-left-color: #d39e00; }

.dsd-clean-msg {
  color: #28a745;
  font-size: 0.85rem;
  padding: 8px 10px;
  background: #eaf7ec;
  border-radius: 6px;
}

.dsd-lowactivity-msg {
  color: #6c757d;
  font-size: 0.85rem;
  padding: 8px 10px;
  background: #f4f5f6;
  border-radius: 6px;
}

@media (max-width: 768px) {
  .dsd-filter-control,
  .dsd-driver-select {
    width: 100% !important;
    max-width: 100% !important;
  }
  /* FI-1988 follow-up: the Fleet Overview summary tiles (Total Drivers, Fleet
     Avg Combined Score, Elite/Solid/Fair/High Risk/Insufficient Data counts) were
     rendering as oversized squares on a phone. Shrink to compact 3-up tiles. */
  .dsd-metrics-row {
    gap: 0.3rem !important;
    margin-bottom: 0.75rem !important;
  }
  .dsd-metrics-row .metric-card-inline {
    flex: 1 1 30% !important;
    min-width: 0 !important;
    padding: 0 !important;
  }
  .dsd-metrics-row .metric-card-inline ::v-deep .card-body {
    padding: 0.3rem 0.3rem !important;
  }
  .dsd-metrics-row .metric-card-inline h5 {
    font-size: 0.6rem !important;
    margin-bottom: 0.1rem !important;
    line-height: 1.1 !important;
    white-space: normal;
  }
  .dsd-metrics-row .metric-card-inline h2 {
    font-size: 0.95rem !important;
    margin-bottom: 0 !important;
    line-height: 1.15 !important;
  }
  .dsd-metrics-row .metric-card-inline span {
    font-size: 0.55rem !important;
  }

}
</style>

<style>
/* modal chrome must be unscoped: bootstrap-vue portals the modal to <body>,
   outside this component's scope attribute (ops_desk precedent). phones get
   a full-screen sheet. */
.dsd-settings-modal .modal-body {
  padding: 16px 18px 22px;
}
@media (max-width: 768px) {
  .dsd-settings-modal .modal-dialog {
    max-width: 100%;
    width: 100%;
    margin: 0;
    min-height: 100%;
  }
  .dsd-settings-modal .modal-content {
    min-height: 100vh;
    border: 0;
    border-radius: 0;
  }
  .dsd-settings-modal .modal-body {
    padding: 12px 14px 20px;
  }
}

</style>
